/* eslint-disable @typescript-eslint/no-explicit-any */
import Script from 'next/script'
import React, { useEffect, useState } from 'react'
import useLogger from 'src/context/LoggerProvider/useLogger'
import firebase from 'src/firebase/firebaseClient'
import { useDebounce } from 'src/hooks/useDebounce'
import { detectInAppBrowser } from 'src/utils/inAppBrowser'
import { authError, login, signUp } from 'src/utils/pixels/spotmeEvents'
import config from '../../config'
import { useAuthProvider } from '../../context/AuthProvider'

/** 5 secs */
const DELAY_TIME = 5 * 1000

function GooglePopup() {
  const { isAuthenticated, loading, cookiesDealt } = useAuthProvider()
  const logger = useLogger()

  // Google One Tap (GSI) is blocked inside in-app browsers, so don't even load
  // the client script there. Detected after mount to stay hydration-safe.
  const [inApp, setInApp] = useState(false)
  useEffect(() => {
    setInApp(detectInAppBrowser())
  }, [])

  const [isReady, setIsReady] = useState(false)
  const delayReady = useDebounce(isReady, DELAY_TIME)
  // `cookiesDealt` = "the cookie banner is not in the way" (IAuthContext) —
  // it is NOT consent. One Tap arms on banner-suppressed landings (hood /
  // listing / anfitrion / flotillas), on desktop while the banner shows, and
  // after a mobile dismiss. Until 2026-09-14 it flipped only on an explicit
  // choice, which left One Tap dark on those landings (0 One Tap sign-ups on
  // Flotillas, where ~7.2k of ~10.4k paid search sessions land) and for the
  // ~36% of visitors who ignore the banner — while One Tap is 51% of sign-ups.
  useEffect(() => {
    if (!loading && !isAuthenticated && cookiesDealt) {
      setIsReady(true)
    }
  }, [isAuthenticated, loading, cookiesDealt])

  useEffect(() => {
    if (loading || isAuthenticated) {
      return
    }

    const w = window as any
    w.handleCredentialResponse = (response: any) => {
      const credential = firebase.auth?.GoogleAuthProvider.credential(
        response.credential
      )

      firebase
        .auth()
        .signInWithCredential(credential)
        .then(({ additionalUserInfo }) => {
          if (additionalUserInfo?.isNewUser) {
            signUp({ method: 'google_popup' })
          } else {
            login({ method: 'google_popup' })
          }
        })
        .catch((error) => {
          authError({ method: 'google_popup', code: error?.code, inApp })
          logger.error('Error Google PopUp login', error)
        })
    }
  }, [loading, isAuthenticated, logger, inApp])

  const uri =
    typeof window === 'object' ? window.location.href : 'https://www.spotme.mx'

  return delayReady && !inApp ? (
    <>
      <Script
        id="google-login"
        defer
        strategy="lazyOnload"
        src="https://accounts.google.com/gsi/client"
      />
      <div
        id="g_id_onload"
        data-client_id={config.googleWebClientId}
        data-login_uri={uri}
        data-callback="handleCredentialResponse"
      />
    </>
  ) : null
}

export default React.memo(GooglePopup)
